import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { isNil } from 'lodash'
import * as LS from 'local-storage'
import { selectors as memberSelectors } from 'member-reducer'
import { API_ERROR_MAP } from 'types'
import { PAYMENT_STAGE, logPaymentFailed } from 'uxcam-service'
import convertDollarsToCents from './convertDollarsToCents'

const usePaymentIntentManager = ({
  amount,
  deleteStripePaymentIntent = null,
  fetchStripePaymentIntent,
  flashErrorMessage,
  stripeCustomerId = null,
  updateStripePaymentIntent,
}) => {
  const stripePaymentIntent = useSelector(memberSelectors.stripePaymentIntent)

  const createOrUpdate = useCallback(
    async (amt) => {
      if (isNil(amt)) return

      const amountInCents = convertDollarsToCents(amt)
      const storedPaymentIntent = LS.getStripePaymentIntent()

      if (storedPaymentIntent?.amount === amountInCents) return

      try {
        if (amountInCents === 0) {
          if (storedPaymentIntent?.payment_intent_id && deleteStripePaymentIntent) {
            return deleteStripePaymentIntent(
              storedPaymentIntent.payment_intent_id,
            )
          }
          return
        }

        if (!storedPaymentIntent?.payment_intent_id) {
          return fetchStripePaymentIntent(amountInCents, stripeCustomerId)
        }

        try {
          await updateStripePaymentIntent({
            amount: amountInCents,
            payment_intent_id: storedPaymentIntent.payment_intent_id,
          })
        } catch {
          LS.clearStripePaymentIntent()
          await fetchStripePaymentIntent(amountInCents, stripeCustomerId)
        }
      } catch (error) {
        logPaymentFailed(PAYMENT_STAGE.PAYMENT_INTENT, error)
        flashErrorMessage(API_ERROR_MAP.ERROR_PROCESSING_PAYMENT)
      }
    },
    [
      deleteStripePaymentIntent,
      fetchStripePaymentIntent,
      flashErrorMessage,
      stripeCustomerId,
      updateStripePaymentIntent,
    ],
  )

  useEffect(() => {
    createOrUpdate(amount)
  }, [amount, createOrUpdate, stripePaymentIntent])

  return {
    paymentIntentId: stripePaymentIntent?.payment_intent_id ?? null,
    clientSecret: stripePaymentIntent?.client_secret ?? null,
    customerSessionSecret:
      stripePaymentIntent?.customer_session_client_secret ?? null,
    stripeCustomerId: stripePaymentIntent?.stripe_customer_id ?? null,
  }
}

export default usePaymentIntentManager
